import { Collection, Database, Q } from '@nozbe/watermelondb';

import ShoppingListProduct from './ShoppingListProduct';
import Organisation from './Organisation';
import { ShoppingListServiceOptions } from '../../types/dbService';
import {
    ShoppingListModel,
    ShoppingListPayload,
} from '../../types/ShoppingList';
import { ShoppingListProductModel } from '../../types/ShoppingListProduct';
import { getLocal } from '../../utils/date';
import { SORT_TYPE } from '../../constants/sort';
import {
    logAddToInventoryActions,
    prepareAddShoppingListToInventory,
} from './helpers/shoppingList/addToInventory';
import {
    addPreparedDataBatch,
    mapRecord,
    mapRecords,
    PreparedShoppingListData,
} from './helpers/shoppingList/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddShoppingListActions,
    prepareAddShoppingListProducts,
    prepareCreateShoppingList,
} from './helpers/shoppingList/shoppingListAdd';
import {
    logDeleteShoppingListActions,
    prepareDeleteShoppingList,
} from './helpers/shoppingList/shoppingListDelete';
import {
    logUpdateShoppingListActions,
    prepareSendShoppingListToDealer,
    prepareUpdateShoppingList,
    prepareUpdateShoppingListName,
} from './helpers/shoppingList/shoppingListUpdate';

class ShoppingList {
    private database: Database;
    private collection: Collection<ShoppingListModel>;
    private table = 'shopping_lists' as const;
    private options: ShoppingListServiceOptions;

    constructor(options: ShoppingListServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll(...queries: Q.Clause[]) {
        return this.collection.query(...queries).fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    observeCount() {
        return this.collection.query().observeCount();
    }

    private filterShoppingLists(searchText?: string) {
        const queries: Q.Clause[] = [];

        if (searchText) {
            const parsedSearchText = searchText.toLowerCase().trim();
            const sanitizedSearch = Q.sanitizeLikeString(parsedSearchText);
            queries.push(
                Q.or(
                    Q.where('name', Q.like(`${sanitizedSearch}%`)),
                    Q.where('name', Q.like(`% ${sanitizedSearch}%`)),
                ),
            );
        }

        return this.collection.query(...queries);
    }

    getFilteredShoppingLists(searchText?: string, sort?: SORT_TYPE) {
        const query = this.filterShoppingLists(searchText);
        return query.extend(Q.sortBy('name', sort ?? SORT_TYPE.ASC));
    }

    getFilteredShoppingListsCount(searchText: string) {
        const query = this.filterShoppingLists(searchText);
        return query.fetchCount();
    }

    getRecentlyCreatedShoppingLists(limit = 1) {
        const today = getLocal();

        const tomorrow = today.plus({ days: 1 });
        const formattedTomorrow = Q.sanitizeLikeString(
            tomorrow.toISO().split('T')[0],
        ).replace(/_/g, '-');

        return this.collection.query(
            Q.where(
                'created_at',
                Q.lt(Q.sanitizeLikeString(formattedTomorrow)),
            ),
            Q.sortBy('created_at', Q.desc),
            Q.take(limit),
        );
    }

    async add(payload: ShoppingListPayload, userId: string) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const organisationID = organisation[0]?.id;

        if (!organisationID) {
            throw new Error(
                'Cannot create shopping list without an active organisation',
            );
        }

        return await this.database.write(async () => {
            const createdShoppingList = prepareCreateShoppingList({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });

            const { shoppingListProducts } = payload;
            const shoppingListProductsToAdd =
                await prepareAddShoppingListProducts({
                    shoppingList: createdShoppingList,
                    shoppingListProducts,
                    userId,
                    serviceOptions: {
                        database: this.database,
                        imageService: this.options.imageService,
                        logDBAction: this.options.logger.logDBAction,
                        validation: this.options.validation,
                    },
                });

            const preparedData: PreparedShoppingListData[] = [
                mapRecord(createdShoppingList, this.table, 'add'),
                ...mapRecords(
                    shoppingListProductsToAdd,
                    'shopping_list_products',
                    'add',
                ),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddShoppingListActions({
                shoppingList: createdShoppingList,
                shoppingListProductsToAdd,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return createdShoppingList;
        });
    }

    async update(id: string, payload: ShoppingListPayload, userId: string) {
        const shoppingListElement = await this.getByID(id);
        const { shoppingListProducts } = payload;

        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const shoppingListProductService = new ShoppingListProduct({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const organisationID = organisation[0]?.id;

        if (!organisationID) {
            throw new Error(
                'Cannot update shopping list without an active organisation',
            );
        }

        let updatedShoppingListProducts: ShoppingListProductModel[] = [];

        return await this.database.write(async () => {
            const shoppingListToUpdate = prepareUpdateShoppingList({
                shoppingListElement,
                name: payload.name,
                organisationID,
            });

            const result = await shoppingListProductService.prepareUpdate(
                shoppingListElement,
                shoppingListProducts,
                userId,
                organisationID,
            );

            const {
                shoppingListProductsToAdd,
                shoppingListProductsToUpdate,
                shoppingListProductsToDelete,
            } = result;

            updatedShoppingListProducts = [
                ...shoppingListProductsToAdd,
                ...shoppingListProductsToUpdate,
            ];

            const preparedData: PreparedShoppingListData[] = [
                mapRecord(shoppingListToUpdate, this.table, 'update'),
                ...mapRecords(
                    shoppingListProductsToAdd,
                    'shopping_list_products',
                    'add',
                ),
                ...mapRecords(
                    shoppingListProductsToUpdate,
                    'shopping_list_products',
                    'update',
                ),
                ...mapRecords(
                    shoppingListProductsToDelete,
                    'shopping_list_products',
                    'delete',
                ),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateShoppingListActions({
                shoppingListElement,
                shoppingListProductsToAdd,
                shoppingListProductsToUpdate,
                shoppingListProductsToDelete,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return updatedShoppingListProducts;
        });
    }

    async addToInventory(id: string, userId: string) {
        const shoppingListElement = await this.getByID(id);
        const shoppingListProducts =
            await shoppingListElement.shoppingListProducts.fetch();

        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        const addedToInventoryTime = getLocal().toISO();

        await this.database.write(async () => {
            const {
                shoppingListToUpdate,
                inventoryProductsToAdd,
                inventoryChanges,
            } = await prepareAddShoppingListToInventory({
                shoppingListElement,
                shoppingListProducts,
                addedToInventoryTime,
                userId,
                organisationID,
                database: this.database,
                serviceOptions: this.options,
            });

            const preparedData: PreparedShoppingListData[] = [
                mapRecord(shoppingListToUpdate, this.table, 'update'),
                ...mapRecords(
                    inventoryProductsToAdd,
                    'inventory_products',
                    'add',
                ),
                ...mapRecords(inventoryChanges, 'inventory_changes', 'add'),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddToInventoryActions({
                inventoryProductsToAdd,
                inventoryChanges,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });
    }

    async sendToDealer(id: string, dealerId: string) {
        const shoppingListElement = await this.getByID(id);

        await this.database.write(async () => {
            const shoppingListToUpdate = prepareSendShoppingListToDealer({
                shoppingListElement,
                dealerId,
            });
            const preparedData: PreparedShoppingListData[] = [
                mapRecord(shoppingListToUpdate, this.table, 'update'),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            this.options.logger.logDBAction({
                message: 'Send to dealer',
                modelName: this.table,
                payload: shoppingListElement,
            });
        });
    }

    async deleteByID(id: string) {
        const shoppingListElement = await this.getByID(id);

        const shoppingListProductService = new ShoppingListProduct({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        await this.database.write(async () => {
            const shoppingListToDelete = prepareDeleteShoppingList({
                shoppingListElement,
            });

            const productsToDelete =
                await shoppingListProductService.prepareDelete(
                    shoppingListElement,
                );
            const preparedData: PreparedShoppingListData[] = [
                mapRecord(shoppingListToDelete, this.table, 'delete'),
                ...mapRecords(
                    productsToDelete,
                    'shopping_list_products',
                    'delete',
                ),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logDeleteShoppingListActions({
                shoppingListElement,
                productsToDelete,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });
    }

    async updateName(id: string, name: string) {
        const shoppingListElement = await this.getByID(id);

        await this.database.write(async () => {
            const shoppingListToUpdate = prepareUpdateShoppingListName({
                shoppingListElement,
                name,
            });
            const preparedData: PreparedShoppingListData[] = [
                mapRecord(shoppingListToUpdate, this.table, 'update'),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            this.options.logger.logDBAction({
                message: 'Update shopping list name',
                modelName: this.table,
                payload: shoppingListElement,
            });
        });
    }
}

export default ShoppingList;
